import type { JSX } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ClientResource } from '@majuscule/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { capLabel, percentUsed } from './client-helpers';
import { formatEur, formatEurCents, formatPercent } from '@/lib/format';
import { useToast } from './toast';
import { internalFetch, internalSend } from '../lib/api';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Meter } from '@/components/ui/chart';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { Field, InputGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Row4 } from '@/components/ui/layout-grid';
import { StatCard } from '@/components/ui/stat-card';

interface ClientUsageResponse {
  month: string;
  spent: string;
  providerCost: string;
}

const EURO = /^\d+(\.\d{1,6})?$/;
const EURO_OR_EMPTY = /^(\d+(\.\d{1,6})?)?$/;

const capsFormSchema = z.object({
  safetyCap: z.string().regex(EURO, 'Montant en euros'),
  clientCap: z.string().regex(EURO_OR_EMPTY, 'Montant en euros').optional(),
  defaultUserCap: z.string().regex(EURO_OR_EMPTY, 'Montant en euros').optional(),
});
type FormValues = z.infer<typeof capsFormSchema>;

/**
 * Indicator card with a threshold gauge under the value (80 % marker). Composed
 * locally: the shared `StatCard` has no slot for a full-width gauge.
 */
function CapStatCard({
  label,
  value,
  spent,
  cap,
}: {
  label: string;
  value: string;
  spent: string | undefined;
  cap: string | null;
}): JSX.Element {
  const pct = spent === undefined ? null : percentUsed(spent, cap);
  let foot: ReactNode;
  if (spent === undefined) {
    foot = 'consommation…';
  } else if (pct === null) {
    foot = `utilisé : ${formatEur(spent)}`;
  } else {
    foot = `utilisé : ${formatEur(spent)} (${formatPercent(pct)})`;
  }
  return (
    <Card className="grid content-start gap-2 p-4">
      <div className="text-label text-muted-foreground">{label}</div>
      <div className="truncate font-mono text-figure-lg tabular-nums">{value}</div>
      {pct !== null ? (
        <Meter percent={pct} ariaLabel={`${formatPercent(pct)} du plafond consommé`} />
      ) : null}
      <div className="text-caption text-muted-foreground">{foot}</div>
    </Card>
  );
}

/**
 * Caps of a client: four indicators (consumption, safety cap, client cap,
 * default user cap) and the edit dialog. The dialog is opened by the page
 * (action « Modifier les plafonds » in the top bar).
 */
export function ClientCaps({
  client,
  editOpen,
  onEditOpenChange,
}: {
  client: ClientResource;
  editOpen: boolean;
  onEditOpenChange: (open: boolean) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const { notify } = useToast();

  const usage = useQuery({
    queryKey: ['client-usage', client.id],
    queryFn: () => internalFetch<ClientUsageResponse>(`/internal/clients/${client.id}/usage`),
  });
  const spent = usage.data?.spent;

  const form = useForm<FormValues>({
    resolver: zodResolver(capsFormSchema),
    defaultValues: {
      safetyCap: client.safetyCap,
      clientCap: client.clientCap ?? '',
      defaultUserCap: client.defaultUserCap ?? '',
    },
  });

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      internalSend('PATCH', `/internal/clients/${client.id}`, {
        safetyCap: values.safetyCap,
        clientCap:
          values.clientCap === undefined || values.clientCap === '' ? null : values.clientCap,
        defaultUserCap:
          values.defaultUserCap === undefined || values.defaultUserCap === ''
            ? null
            : values.defaultUserCap,
      }),
    onSuccess: () => {
      onEditOpenChange(false);
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['cap-changes', client.id] });
      notify('Plafonds mis à jour');
    },
  });

  // Each opening starts from the current caps, without the previous error. A
  // refetch of the client while the dialog is open does not wipe the edits.
  const clientRef = useRef(client);
  clientRef.current = client;
  const { reset: resetForm } = form;
  const { reset: resetSave } = save;
  useEffect(() => {
    if (!editOpen) {
      return;
    }
    const current = clientRef.current;
    resetForm({
      safetyCap: current.safetyCap,
      clientCap: current.clientCap ?? '',
      defaultUserCap: current.defaultUserCap ?? '',
    });
    resetSave();
  }, [editOpen, resetForm, resetSave]);

  const errors = form.formState.errors;
  const close = (): void => {
    onEditOpenChange(false);
  };

  return (
    <>
      <Row4 aria-label="Consommation et plafonds">
        <StatCard
          label="Consommation du mois"
          value={spent === undefined ? '…' : formatEurCents(spent)}
          foot="mois en cours, HT, en-cours inclus"
        />
        <CapStatCard
          label="Plafond de sécurité"
          value={formatEur(client.safetyCap)}
          spent={spent}
          cap={client.safetyCap}
        />
        <CapStatCard
          label="Plafond client"
          value={capLabel(client.clientCap)}
          spent={spent}
          cap={client.clientCap}
        />
        <StatCard
          label="Plafond utilisateur par défaut"
          value={capLabel(client.defaultUserCap)}
          foot="appliqué à la création"
        />
      </Row4>

      <Dialog
        open={editOpen}
        onOpenChange={(next) => {
          if (!next) {
            close();
          }
        }}
      >
        <DialogContent>
          <DialogHead
            title="Modifier les plafonds"
            description="Montants mensuels en euros. Vide = aucun plafond."
          />
          <form
            id="caps-form"
            onSubmit={(event) => {
              void form.handleSubmit((values) => {
                save.mutate(values);
              })(event);
            }}
            className="contents"
          >
            <DialogBody>
              <Field
                label="Plafond de sécurité"
                required
                hint="Obligatoire. Met le client en pause au dépassement."
                error={errors.safetyCap?.message}
              >
                <InputGroup suffix="€">
                  <Input
                    numeric
                    inputMode="decimal"
                    placeholder="1000"
                    {...form.register('safetyCap')}
                  />
                </InputGroup>
              </Field>
              <Field
                label="Plafond client"
                hint="Refuse les requêtes au dépassement."
                error={errors.clientCap?.message}
              >
                <InputGroup suffix="€">
                  <Input
                    numeric
                    inputMode="decimal"
                    placeholder="Optionnel"
                    {...form.register('clientCap')}
                  />
                </InputGroup>
              </Field>
              <Field
                label="Plafond utilisateur par défaut"
                hint="Appliqué au plafond d'un nouvel utilisateur sans plafond explicite."
                error={errors.defaultUserCap?.message}
              >
                <InputGroup suffix="€">
                  <Input
                    numeric
                    inputMode="decimal"
                    placeholder="Optionnel"
                    {...form.register('defaultUserCap')}
                  />
                </InputGroup>
              </Field>
              {save.isError ? (
                <Callout variant="destructive">
                  Échec (le plafond par défaut dépasse peut-être le plafond client).
                </Callout>
              ) : null}
            </DialogBody>
          </form>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              Annuler
            </Button>
            <Button type="submit" form="caps-form" size="lg" disabled={save.isPending}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
